import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Award,
  Building2,
  Eye,
  Globe,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge as ShadBadge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'
import {
  useAdminBadges,
  useCreateBadge,
  useDeleteBadge,
  useUpdateBadge,
  type RosterGrantSummary,
} from '@/services/admin/badges.service'
import { useCompanies } from '@/services/admin/companies.service'
import { useTrophyQueue } from '@/hooks/use-trophy-queue'
import type { UserBadge } from '@/services/badges.service'

import { BadgeFormDialog } from './BadgeFormDialog'
import { BadgeMedallion } from './BadgeMedallion'
import { formatBrDate } from './format'
import { RECURRENCE_OPTIONS, TIERS } from './options'
import './styles.css'
import type { Badge, BadgeDraft, Criterion, Scope, TemporalWindow, Tier } from './types'

type TierFilter = Tier | 'all'
type ScopeFilter = 'all' | 'global' | 'company'

const tierLabel: Record<Tier, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
  platina: 'Platina',
}

/**
 * Listagem de badges do super_admin. Integrada com a API real do back
 * (10 endpoints em /api/admin/badges). Cache via React Query — onSuccess
 * de cada mutation invalida a query principal.
 */
export function BadgesManagement() {
  const { toast } = useToast()
  const { data: badges, isLoading, isError, error } = useAdminBadges()
  const createBadge = useCreateBadge()
  const updateBadge = useUpdateBadge()
  const deleteBadge = useDeleteBadge()
  // Fila do overlay de troféu — usada só pra pré-visualização (abaixo).
  const { enqueue } = useTrophyQueue()

  const [query, setQuery] = useState('')
  const [tierFilter, setTierFilter] = useState<TierFilter>('all')
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Badge | null>(null)
  // Badge alvo do AlertDialog de confirmação. null = dialog fechado.
  const [deleting, setDeleting] = useState<Badge | null>(null)

  // `badges ?? []` na linha do JSX criaria uma referência nova a cada render
  // quando a query ainda não tem dados, invalidando os useMemo abaixo.
  // Memoizando aqui mantemos a referência estável até que `badges` mude.
  const items = useMemo(() => badges ?? [], [badges])

  const filtered = useMemo(() => {
    return items
      .filter((b) => (tierFilter === 'all' ? true : b.tier === tierFilter))
      .filter((b) =>
        scopeFilter === 'all'
          ? true
          : scopeFilter === 'global'
            ? b.scope.type === 'global'
            : b.scope.type === 'company'
      )
      .filter((b) =>
        query.trim() ? b.name.toLowerCase().includes(query.trim().toLowerCase()) : true
      )
      .sort((a, b) => a.order - b.order)
  }, [items, tierFilter, scopeFilter, query])

  const stats = useMemo(
    () => ({
      total: items.length,
      active: items.filter((b) => b.active).length,
      global: items.filter((b) => b.scope.type === 'global').length,
      company: items.filter((b) => b.scope.type === 'company').length,
    }),
    [items]
  )

  function handleNew() {
    setEditing(null)
    setFormOpen(true)
  }

  function handleEdit(badge: Badge) {
    setEditing(badge)
    setFormOpen(true)
  }

  /**
   * Pré-visualiza a notificação de desbloqueio: enfileira a badge no
   * overlay de troféu (mesmo banner + som de quando o usuário ganha de
   * verdade). É puro estado de UI — NÃO concede a badge, não toca o
   * back nem o seen-set. `cycleKey` único por clique permite repetir.
   */
  function previewBadge(badge: Badge) {
    const preview: UserBadge = {
      badge,
      earnedAt: new Date().toISOString(),
      cycleKey: `preview-${Date.now()}`,
      source: 'manual',
    }
    enqueue([preview])
  }

  async function handleSubmit(draft: BadgeDraft, recipientEmails?: string[]) {
    try {
      const result = editing
        ? await updateBadge.mutateAsync({
            id: editing.id,
            patch: { ...draft, recipientEmails },
          })
        : await createBadge.mutateAsync({ ...draft, recipientEmails })
      toast({
        title: editing ? 'Badge atualizada' : 'Badge criada',
        description: result.rosterGrant
          ? describeRosterGrant(result.rosterGrant)
          : `"${draft.name}" foi ${editing ? 'atualizada' : 'adicionada ao catálogo'}.`,
      })
      setFormOpen(false)
      setEditing(null)
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: editing ? 'Erro ao atualizar badge' : 'Erro ao criar badge',
        description: getErrorMessage(err, 'Ocorreu um erro ao processar a solicitação.'),
      })
    }
  }

  async function handleConfirmDelete() {
    if (!deleting) return
    try {
      await deleteBadge.mutateAsync(deleting.id)
      toast({
        title: 'Badge desativada',
        description: `"${deleting.name}" foi removida do catálogo (soft delete).`,
      })
      setDeleting(null)
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar badge',
        description: getErrorMessage(err, 'Ocorreu um erro ao deletar a badge.'),
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header ------------------------------------------------ */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Catálogo de Badges
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Conquistas globais ou por empresa — combine critério, janela e recorrência.
            </p>
          </div>
          <Button onClick={handleNew} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Badge
          </Button>
        </div>
      </motion.div>

      {/* Stats ----------------------------------------------------- */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <StatTile label="Catalogadas" value={stats.total} accent />
        <StatTile label="Ativas" value={stats.active} />
        <StatTile label="Globais" value={stats.global} icon={<Globe className="w-4 h-4" />} />
        <StatTile
          label="Por empresa"
          value={stats.company}
          icon={<Building2 className="w-4 h-4" />}
        />
      </motion.div>

      {/* Filtros + Tabela ------------------------------------------ */}
      <Card className="border-border/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Badges cadastradas
              </CardTitle>
              <CardDescription className="mt-1">
                {isLoading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Carregando...
                  </span>
                ) : (
                  <>
                    {filtered.length} {filtered.length === 1 ? 'badge exibida' : 'badges exibidas'}{' '}
                    de {stats.total} total
                  </>
                )}
              </CardDescription>
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground/70">
                <Eye className="h-3 w-3" />
                Clique na medalha para pré-visualizar a notificação de desbloqueio.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-[220px] pl-9"
                  disabled={isLoading}
                />
              </div>

              <Select
                value={tierFilter}
                onValueChange={(v) => setTierFilter(v as TierFilter)}
                disabled={isLoading}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tiers</SelectItem>
                  {TIERS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">
                        <span className="bdg-tier-dot" data-tier={t.value} />
                        {t.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={scopeFilter}
                onValueChange={(v) => setScopeFilter(v as ScopeFilter)}
                disabled={isLoading}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Escopo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os escopos</SelectItem>
                  <SelectItem value="global">Globais</SelectItem>
                  <SelectItem value="company">Por empresa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState message={getErrorMessage(error, 'Erro ao carregar badges.')} />
          ) : filtered.length === 0 ? (
            <EmptyState onCreate={handleNew} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[72px]">Medalha</TableHead>
                  <TableHead>Nome & critério</TableHead>
                  <TableHead className="w-[130px]">Tier · Pontos</TableHead>
                  <TableHead className="w-[150px]">Escopo</TableHead>
                  <TableHead className="w-[230px]">Janela · Recorrência</TableHead>
                  <TableHead className="w-[100px] text-center">Conquistas</TableHead>
                  <TableHead className="w-[100px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} className="group">
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => previewBadge(b)}
                        title="Pré-visualizar a notificação de desbloqueio"
                        aria-label={`Pré-visualizar notificação de ${b.name}`}
                        className="relative rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <BadgeMedallion
                          tier={b.tier}
                          iconUrl={b.iconUrl}
                          inscription={b.name}
                          size={48}
                        />
                        <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100">
                          <Eye className="h-3 w-3" />
                        </span>
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{b.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {summarizeCriterion(b.criterion)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="bdg-tier-dot" data-tier={b.tier} />
                        <div className="flex flex-col leading-tight">
                          <span className="text-xs font-medium text-foreground">
                            {tierLabel[b.tier]}
                          </span>
                          <span className="text-[11px] text-muted-foreground">{b.points} pts</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ScopeBadge scope={b.scope} />
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-foreground">{summarizeWindow(b.window)}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        ↻{' '}
                        {RECURRENCE_OPTIONS.find(
                          (r) => r.value === b.recurrence
                        )?.label.toLowerCase()}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="text-base font-semibold text-foreground">
                        {b.earnedCount ?? 0}
                      </div>
                      <div className="text-[11px] text-muted-foreground">users</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(b)}
                          className="gap-1.5"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleting(b)}
                          className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Deletar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BadgeFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        badge={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar esta badge?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" será marcada como inativa (soft delete) e não aparecerá mais pros
              usuários finais. Conquistas já obtidas pelos usuários são preservadas. Você pode
              reativar depois no banco se mudar de ideia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteBadge.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBadge.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ================================================================= //
// AUX                                                               //
// ================================================================= //

function StatTile({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon?: React.ReactNode
  accent?: boolean
}) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
            <div
              className={`text-2xl font-bold mt-1 ${accent ? 'text-primary' : 'text-foreground'}`}
            >
              {value}
            </div>
          </div>
          {icon && <div className="text-muted-foreground">{icon}</div>}
        </div>
      </CardContent>
    </Card>
  )
}

function ScopeBadge({ scope }: { scope: Scope }) {
  // Hook chamado mesmo pra scope global porque a regra de hooks proíbe
  // chamar condicional. O React Query cacheia, então o custo é o de
  // uma referência (zero rede em re-renders).
  const { data: companies } = useCompanies()

  if (scope.type === 'global') {
    return (
      <ShadBadge variant="outline" className="gap-1.5">
        <Globe className="w-3 h-3" />
        Global
      </ShadBadge>
    )
  }

  // Resolve o nome real da empresa via lookup; fallback 'Empresa' enquanto
  // companies carrega ou se a empresa sumiu da lista (inativa/deletada).
  const company = companies?.find((c) => c.id === scope.companyId)
  return (
    <ShadBadge
      variant="default"
      className="gap-1.5 bg-primary/15 text-primary border-primary/30 hover:bg-primary/20"
    >
      <Building2 className="w-3 h-3" />
      {company?.name ?? 'Empresa'}
    </ShadBadge>
  )
}

function LoadingState() {
  return (
    <div data-testid="badges-loading" className="space-y-3 py-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <Award className="w-8 h-8 text-destructive" />
      </div>
      <h3 className="text-lg font-semibold text-foreground">Erro ao carregar badges</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">{message}</p>
    </div>
  )
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <Award className="w-8 h-8 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-foreground">Nenhuma badge encontrada</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">
        Não há badges que correspondam aos filtros atuais. Tente limpar os filtros ou criar uma nova
        badge.
      </p>
      <Button onClick={onCreate} className="mt-5 gap-2">
        <Plus className="w-4 h-4" />
        Criar primeira badge
      </Button>
    </div>
  )
}

function summarizeCriterion(c: Criterion): string {
  switch (c.type) {
    case 'specific_episode':
      return `Episódio específico (${c.targetId || 'sem alvo'})`
    case 'specific_module':
      return `Módulo específico (${c.targetId || 'sem alvo'})`
    case 'specific_course':
      return `Curso específico (${c.targetId || 'sem alvo'})`
    case 'count_episodes':
      return `${c.count} episódios concluídos`
    case 'count_modules':
      return `${c.count} módulos concluídos`
    case 'count_courses':
      return `${c.count} cursos concluídos`
    case 'streak_days':
      return `Streak de ${c.count} dias`
    case 'day_marathon':
      return `${c.count} episódios no mesmo dia`
    case 'quiz_perfect_streak':
      return `${c.count} quizzes 100% seguidos`
    case 'return_after_idle':
      return `Retorno após ${c.count}+ dias inativo`
    case 'any_activity':
      return 'Qualquer atividade no dia'
    case 'account_created':
      return 'Cadastro na plataforma'
    case 'quiz_perfect':
      return c.quizId ? `Quiz "${c.quizId}" 100%` : 'Qualquer quiz 100%'
    case 'module_all_quizzes_perfect':
      return c.moduleId
        ? `Todos os quizzes do módulo "${c.moduleId}"`
        : 'Todos os quizzes de um módulo'
    case 'quiz_comeback':
      return 'Errar e voltar pra 100%'
    case 'manual_roster':
      return 'Lista manual de usuários'
  }
}

/** Frase do resumo da concessão em lote (manual_roster), pro toast. */
function describeRosterGrant(rg: RosterGrantSummary): string {
  const parts = [`${rg.granted} concedida${rg.granted === 1 ? '' : 's'}`]
  if (rg.alreadyHad > 0) {
    parts.push(`${rg.alreadyHad} já tinha${rg.alreadyHad === 1 ? '' : 'm'}`)
  }
  if (rg.notFound.length > 0) {
    parts.push(`${rg.notFound.length} email(s) não encontrado(s)`)
  }
  if (rg.skipped > 0) {
    parts.push(`${rg.skipped} fora do escopo`)
  }
  return parts.join(' · ')
}

function summarizeWindow(w: TemporalWindow): string {
  switch (w.type) {
    case 'always':
      return 'Sempre vale'
    case 'between':
      return `${formatBrDate(w.start)} → ${formatBrDate(w.end)}`
    case 'specific_date':
      return formatBrDate(w.date)
    case 'recurring_yearly': {
      const [m, d] = w.monthDay.split('-').map(Number)
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')} de cada ano`
    }
    case 'recurring_monthly':
      return `Dia ${w.dayOfMonth} de cada mês`
    case 'recurring_weekly': {
      const labels = ['', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']
      return `Toda ${labels[w.weekday] || '—'}`
    }
  }
}
